import type { ComponentProps } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { LeadsExportOptionsDropdown } from "app/pages/sales-intelligence/modules/leads-search";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { selectExcelQuota } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import WebsitesDynamicsExportItemContainer from "../../../management/containers/WebsitesDynamicsExportItem/WebsitesDynamicsExportItemContainer";
import WebsitesHubSpotExportItemContainer from "../../../management/containers/WebsitesHubSpotExportItem/WebsitesHubSpotExportItemContainer";
import WebsitesSalesforceExportItemContainer from "../../../management/containers/WebsitesSalesforceExportItem/WebsitesSalesforceExportItemContainer";
import WebsitesExcelExportItemContainer from "../WebsitesExcelExportItem/WebsitesExcelExportItemContainer";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

type TooltipDisabledProps = ComponentProps<typeof LeadsExportOptionsDropdown>["tooltipDisabledProps"];

type Props = {
    isDisabled?: boolean;
    tooltipProps?: TooltipDisabledProps;
    onOptionSelect(variant: string): void;
};

const WebsitesExportOptionsDropdownContainer = (props: Props) => {
    const exportQuota = useSelector(selectExcelQuota);

    const quotaIsAvailable = exportQuota.remaining > 0;
    const isDisabled = props.isDisabled || !quotaIsAvailable;

    const quotaNotAllowedTooltipContent = quotaIsAvailable ? undefined : translate("si.websites-export.disabled.text");
    const crmNotAllowedTooltipContent = salesSettingsHelper.isCrmIntegrationsEnabled()
        ? undefined
        : translate("si.websites-export.disabled.text");

    function handleSelect(option: string) {
        trackingService.trackMultiSelectorOpen(option);
        props.onOptionSelect(option);
    }

    const getTooltipProps = (): Readonly<TooltipDisabledProps> => {
        switch (true) {
            case !quotaIsAvailable: {
                const key =
                    exportQuota.total === 0
                        ? "si.websites-export.disabled.text"
                        : "si.websites-export.quota-reached.text";

                return { tooltipContent: translate(key) };
            }

            case !is.undefined(props.tooltipProps):
                return props.tooltipProps;

            default:
                return undefined;
        }
    };

    return (
        <LeadsExportOptionsDropdown
            isDisabled={isDisabled}
            tooltipDisabledProps={getTooltipProps()}
            onOpen={() => trackingService.trackMultiSelectorMenuOpen(exportQuota.remaining)}
        >
            <WebsitesExcelExportItemContainer onClick={() => handleSelect("EXCEL")} />
            <WebsitesSalesforceExportItemContainer
                onClick={() => handleSelect("EXPORT_SALES_FORCE")}
                tooltipDisabledContent={quotaNotAllowedTooltipContent || crmNotAllowedTooltipContent}
                isUseLockedIcon={false}
            />
            <WebsitesHubSpotExportItemContainer
                onClick={() => handleSelect("EXPORT_HUBSPOT")}
                tooltipDisabledContent={quotaNotAllowedTooltipContent || crmNotAllowedTooltipContent}
                isUseLockedIcon={false}
            />
            <WebsitesDynamicsExportItemContainer
                onClick={() => handleSelect("EXPORT_DYNAMICS")}
                tooltipDisabledContent={quotaNotAllowedTooltipContent || crmNotAllowedTooltipContent}
                isUseLockedIcon={false}
            />
        </LeadsExportOptionsDropdown>
    );
};

export default WebsitesExportOptionsDropdownContainer;
